import { ContextualMenuButton } from 'app/design/icons/contextual-menu-button';
import { InsuredBadge } from 'app/design/icons/insured-badge';
import { Button } from 'app/design/primitives/button';
import { Box, Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { useIsCurrentlyUnavailable } from 'app/hooks/use-is-currently-unavailable';
import { localeAwareFormatDistance } from 'app/lib/date';
import { useGetInterlocutor } from 'app/network/chat';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { GroupAvatarStack } from 'app/screens/chat/components/group-avatar-stack';
import { CustomChannelType, isChannelSilenced, type Channel, type ChatUser } from 'app/types/chat';
import { useUser } from 'app/network/user';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Modal, Pressable, StyleSheet, View, type StyleProp, type ViewStyle, useWindowDimensions } from 'react-native';
import { Clubs } from 'app/design/icons/clubs';
import { Leave } from 'app/design/icons/leave';
import { Unread } from 'app/design/icons/unread';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH, isMobileNative } from 'app/utils/constants';
import {
  IconBell,
  IconBellOff,
  IconChevronDown,
  IconChevronLeft,
  IconClock,
  IconPencil,
  IconPlus,
} from '@tabler/icons-react-native';
import { OpenLink } from 'app/design/icons/open-link';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { useUISize } from 'app/utils/web-helpers';
import {
  isTransportSupportChannel,
  TransportSupportAvatar,
} from 'app/screens/chat/components/transport-support-avatar';
const theme = createDSTheme();

interface ChannelTitleMenuButtonProps {
  channel: Channel;
  silenced?: boolean;
  isUpdatingSilence?: boolean;
  onMarkAsUnread?: () => void;
  onToggleSilenced?: (silenced: boolean) => void;
  onAddMembers?: () => void;
  onLeave?: () => void;
  onLinkOffer?: () => void;
  onRename?: () => void;
  isAdmin?: boolean;
}

interface ChannelTitleProps extends ChannelTitleMenuButtonProps {
  style?: StyleProp<ViewStyle>;
  onPrimaryPress?: () => void;
  onChevronPress?: () => void;
  showChevron?: boolean;
  showBackButton?: boolean;
  embeddedInDrawer?: boolean;
  showContextMenu?: boolean;
}

function navigateToChannelMembers(navigation: ReturnType<typeof useNavigation>, channel: Channel) {
  const routeNames = navigation.getState().routeNames;
  if (routeNames.includes('chat-members')) {
    navigation.navigate('chat-members', { url: channel.channel_url }, { pop: true });
    return;
  }

  // Footer chat can be rendered outside the chat navigator (e.g. landing page).
  navigation.navigate(
    'chat',
    {
      screen: 'chat-members',
      params: { url: channel.channel_url },
    },
    { pop: true },
  );
}

function ChannelAvatar({
  channel,
  image,
  isGroupConversation,
  myUserId,
  showTransportSupportAvatar,
  useHauteGaspesieQuickChatStyle,
}: {
  channel: Channel;
  image?: string;
  isGroupConversation: boolean;
  myUserId?: string;
  showTransportSupportAvatar: boolean;
  useHauteGaspesieQuickChatStyle: boolean;
}) {
  if (showTransportSupportAvatar) return <TransportSupportAvatar size={36} />;
  if (image) {
    return (
      <PCImage
        aspectRatio={1}
        height={36}
        source={{ uri: image }}
        style={{
          borderColor: useHauteGaspesieQuickChatStyle ? theme.colors['border.inverse.primary'] : undefined,
          borderRadius: 18,
          borderWidth: useHauteGaspesieQuickChatStyle ? theme.borderWidths['border-width.subtle'] : undefined,
        }}
      />
    );
  }
  if (isGroupConversation) return <GroupAvatarStack members={channel.members} myUserId={myUserId} size={42} />;
  return null;
}

function ChannelSubtitle({
  groupSubtitle,
  interlocutor,
  isGroupConversation,
  isUnavailable,
  showSubtext,
  subtitleColor,
}: {
  groupSubtitle?: string;
  interlocutor?: ChatUser;
  isGroupConversation: boolean;
  isUnavailable: boolean;
  showSubtext: boolean;
  subtitleColor: React.ComponentProps<typeof Text>['color'];
}) {
  const { t } = useTranslation('one-channel');

  if (!isGroupConversation && isUnavailable) {
    return (
      <Row alignItems="center" gap="spacing.general.x1">
        <IconClock strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['white-label.header.on-header']} />
        <Text color="white-label.header.on-header">{t('currentlyUnavailable', { ns: 'neighbour-profile' })}</Text>
      </Row>
    );
  }

  if (isGroupConversation && groupSubtitle) {
    return (
      <Text color={subtitleColor} fontSize={12} numberOfLines={1}>
        {groupSubtitle}
      </Text>
    );
  }

  if (isGroupConversation || !showSubtext || !interlocutor || interlocutor.isSupport) return null;

  return (
    <Text color={subtitleColor} fontSize={12} numberOfLines={1}>
      {interlocutor.trust_points ? `👍 (${interlocutor.trust_points.toFixed(0)})` : ''}
      {interlocutor.trust_points && interlocutor.last_seen ? ' | ' : ''}
      {!interlocutor.last_seen
        ? ''
        : t('lastActiveAgo', {
            ago: localeAwareFormatDistance(new Date(), new Date(interlocutor.last_seen)),
            ns: 'common',
          })}
    </Text>
  );
}

function ChannelMenuItem({
  color = 'text.primary',
  icon,
  label,
  onPress,
}: {
  color?: React.ComponentProps<typeof Text>['color'];
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable onPress={onPress}>
      <Row alignItems="center" gap="spacing.general.x2">
        <Box alignItems="center" width={24}>
          {icon}
        </Box>
        <Text color={color} fontSize={16}>
          {label}
        </Text>
      </Row>
    </Pressable>
  );
}

interface ChannelMenuContentProps extends ChannelTitleMenuButtonProps {
  canViewMembers: boolean;
  effectiveSilenced: boolean;
  handleAddMembers: () => void;
  handleLeave: () => void;
  handleLinkOffer: () => void;
  handleMarkAsUnread: () => void;
  handleRename: () => void;
  handleToggleSilenced: () => void;
  isGroupConversation: boolean;
  openMembersSheet: () => void;
}

function ChannelMenuContent({
  canViewMembers,
  effectiveSilenced,
  handleAddMembers,
  handleLeave,
  handleLinkOffer,
  handleMarkAsUnread,
  handleRename,
  handleToggleSilenced,
  isAdmin,
  isGroupConversation,
  onAddMembers,
  onLeave,
  onLinkOffer,
  onMarkAsUnread,
  onRename,
  onToggleSilenced,
  openMembersSheet,
}: ChannelMenuContentProps) {
  const { t } = useTranslation('one-channel');
  return (
    <>
      {isGroupConversation && isAdmin && onAddMembers ? (
        <ChannelMenuItem
          icon={<IconPlus strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />}
          label={t('addMembers')}
          onPress={handleAddMembers}
        />
      ) : null}
      {canViewMembers ? (
        <ChannelMenuItem
          icon={<Clubs color={theme.colors['text.primary']} height={24} width={24} />}
          label={t('viewMembers')}
          onPress={openMembersSheet}
        />
      ) : null}
      {isGroupConversation && isAdmin && onRename ? (
        <ChannelMenuItem
          icon={<IconPencil strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />}
          label={t('renameConversation')}
          onPress={handleRename}
        />
      ) : null}
      {onMarkAsUnread ? (
        <ChannelMenuItem
          icon={<Unread color={theme.colors['text.primary']} height={24} width={24} />}
          label={t('markAsUnreadButton', { ns: 'all-channels' })}
          onPress={handleMarkAsUnread}
        />
      ) : null}
      {onLinkOffer ? (
        <ChannelMenuItem
          icon={<OpenLink color={theme.colors['text.primary']} height={24} width={24} />}
          label={t('linkOffer')}
          onPress={handleLinkOffer}
        />
      ) : null}
      {onToggleSilenced ? (
        <ChannelMenuItem
          icon={
            effectiveSilenced ? (
              <IconBell strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
            ) : (
              <IconBellOff strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
            )
          }
          label={effectiveSilenced ? t('unmuteConversation') : t('muteConversation')}
          onPress={handleToggleSilenced}
        />
      ) : null}
      {isGroupConversation && onLeave ? (
        <ChannelMenuItem
          color="icon.error"
          icon={
            <Leave
              color={theme.colors['icon.error']}
              style={{ transform: [{ rotate: '90deg' }] }}
              height={24}
              width={24}
            />
          }
          label={t('leaveConversation')}
          onPress={handleLeave}
        />
      ) : null}
    </>
  );
}

function getChannelTitleViewModel({
  channel,
  embeddedInDrawer,
  hasContextMenu,
  interlocutor,
  isLargeUI,
  isUnavailable,
  onPrimaryPress,
  showChevron,
  showBackButton,
  silenced,
}: {
  channel: Channel;
  embeddedInDrawer?: boolean;
  hasContextMenu: boolean;
  interlocutor?: ChatUser;
  isLargeUI: boolean;
  isUnavailable: boolean;
  onPrimaryPress?: () => void;
  showChevron?: boolean;
  showBackButton?: boolean;
  silenced?: boolean;
}) {
  const canViewMembers = channel.custom_type === CustomChannelType.Group || channel.member_count > 2;
  const isGroupConversation = channel.custom_type === CustomChannelType.Group;
  const showSubtext = Boolean(interlocutor?.trust_points || interlocutor?.last_seen || canViewMembers);
  return {
    canViewMembers,
    effectiveSilenced: silenced ?? isChannelSilenced(channel),
    hasContextMenu,
    image: isGroupConversation ? channel.picture : interlocutor?.profile_url,
    isGroupConversation,
    // Embedded native channel headers keep their explicit back control unless
    // the caller delegates it to the navigator.
    showBackButton: showBackButton ?? ((isMobileNative || !isLargeUI) && !onPrimaryPress && !showChevron),
    showSubtext,
    showSubtextOrUnavailable: showSubtext || isUnavailable,
    showTransportSupportAvatar: isTransportSupportChannel(channel),
    subtitleColor: embeddedInDrawer ? 'white-label.header.on-header' : 'white-label.header.on-header',
    title: isGroupConversation ? channel.name : (interlocutor?.nickname ?? channel.name),
    useHauteGaspesieQuickChatStyle: Boolean(showChevron) && isWhiteLabel(WhiteLabel.HauteGaspesie),
  } as const;
}

function getChannelPrimaryPress({
  canViewMembers,
  channel,
  interlocutor,
  navigation,
  onPrimaryPress,
}: {
  canViewMembers: boolean;
  channel: Channel;
  interlocutor?: ChatUser;
  navigation: ReturnType<typeof useNavigation>;
  onPrimaryPress?: () => void;
}) {
  if (onPrimaryPress) return onPrimaryPress;
  if (canViewMembers) return () => navigateToChannelMembers(navigation, channel);
  return () => {
    if (interlocutor?.user_id) navigateToUserProfile(navigation, interlocutor.user_id);
  };
}

function navigateBackFromChannel(navigation: ReturnType<typeof useNavigation>) {
  if (navigation.canGoBack()) {
    navigation.goBack();
    return;
  }

  if (navigation.getState().routeNames.includes('all-channels')) {
    navigation.navigate('all-channels', undefined, { pop: true });
    return;
  }

  navigation.navigate('chat', { screen: 'all-channels' }, { pop: true });
}

export function ChannelBackButton({ show = true }: { show?: boolean }) {
  const { t } = useTranslation('one-channel');
  const navigation = useNavigation();
  if (!show) return null;

  return (
    <Pressable
      accessibilityLabel={t('goBack', { ns: 'accessibility' })}
      accessibilityRole="button"
      hitSlop={8}
      onPress={() => navigateBackFromChannel(navigation)}
    >
      <IconChevronLeft
        color={theme.colors['white-label.header.on-header']}
        size={theme.spacing['spacing.general.x7']}
        strokeWidth={ICON_STROKE_WIDTH}
      />
    </Pressable>
  );
}

function ChannelIdentity({
  availability,
  groupSubtitle,
  interlocutor,
  viewModel,
}: {
  availability: 'available' | 'unavailable';
  groupSubtitle?: string;
  interlocutor?: ChatUser;
  viewModel: ReturnType<typeof getChannelTitleViewModel>;
}) {
  return (
    <Column flex={1} minWidth={0} maxWidth={200}>
      <Row alignItems="center" gap="spacing.general.x1">
        <Text
          color="white-label.header.on-header"
          fontSize={16}
          fontWeight="600"
          numberOfLines={1}
          style={{ flexShrink: 1, lineHeight: viewModel.showSubtextOrUnavailable ? undefined : 32 }}
        >
          {viewModel.title}
        </Text>
        {!viewModel.isGroupConversation && interlocutor?.insured ? (
          <InsuredBadge color={theme.colors['surface.tertiary']} height={20} width={20} />
        ) : null}
        {viewModel.effectiveSilenced ? (
          <IconBellOff strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['white-label.header.on-header']} />
        ) : null}
      </Row>
      <ChannelSubtitle
        groupSubtitle={groupSubtitle}
        interlocutor={interlocutor}
        isGroupConversation={viewModel.isGroupConversation}
        isUnavailable={availability === 'unavailable'}
        showSubtext={viewModel.showSubtext}
        subtitleColor={viewModel.subtitleColor}
      />
    </Column>
  );
}

function ChannelTitleActions({
  hasContextMenu,
  onChevronPress,
  showChevron,
  showContextMenu,
  useHauteGaspesieQuickChatStyle,
  ...menuProps
}: ChannelTitleMenuButtonProps & {
  hasContextMenu: boolean;
  onChevronPress?: () => void;
  showChevron?: boolean;
  showContextMenu: boolean;
  useHauteGaspesieQuickChatStyle: boolean;
}) {
  const { t } = useTranslation('one-channel');
  return (
    <>
      {showContextMenu && hasContextMenu ? <ChannelTitleMenuButton {...menuProps} /> : null}
      {showChevron ? (
        <Pressable
          accessibilityLabel={t('chat.minimizeConversation', { ns: 'accessibility' })}
          accessibilityRole="button"
          hitSlop={12}
          onPress={onChevronPress}
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
            paddingLeft: theme.spacing['spacing.general.x1'],
          }}
        >
          <IconChevronDown
            color={
              useHauteGaspesieQuickChatStyle
                ? theme.colors['icon.inverse.primary']
                : theme.colors['white-label.header.on-header']
            }
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </Pressable>
      ) : null}
    </>
  );
}

export function ChannelTitle({
  channel,
  style,
  onPrimaryPress,
  onChevronPress,
  showChevron,
  showBackButton,
  embeddedInDrawer,
  silenced,
  onMarkAsUnread,
  onToggleSilenced,
  onAddMembers,
  onLeave,
  onLinkOffer,
  onRename,
  isAdmin,
  showContextMenu = true,
}: ChannelTitleProps) {
  const navigation = useNavigation();
  const getInterlocutor = useGetInterlocutor();
  const interlocutor = getInterlocutor(channel);
  const { t } = useTranslation('one-channel');
  const { data: me } = useUser();
  const { isLargeUI } = useUISize();
  const isUnavailable = useIsCurrentlyUnavailable(interlocutor?.unavailability);
  const viewModel = getChannelTitleViewModel({
    channel,
    embeddedInDrawer,
    hasContextMenu: Boolean(onMarkAsUnread || onToggleSilenced || onAddMembers || onLeave || onLinkOffer || onRename),
    interlocutor,
    isLargeUI,
    isUnavailable,
    onPrimaryPress,
    showChevron,
    showBackButton,
    silenced,
  });
  const handlePrimaryPress = getChannelPrimaryPress({
    canViewMembers: viewModel.canViewMembers,
    channel,
    interlocutor,
    navigation,
    onPrimaryPress,
  });
  const groupSubtitle = viewModel.canViewMembers ? t('membersCount', { count: channel.member_count }) : undefined;

  return (
    <Row
      alignItems="center"
      backgroundColor="white-label.header.bg"
      gap="spacing.general.x3"
      style={[style, { padding: isMobileNative ? 0 : theme.spacing['spacing.general.x2'] }]}
      flex={1}
    >
      {viewModel.showBackButton ? <ChannelBackButton /> : null}

      <Pressable accessibilityRole="button" onPress={handlePrimaryPress} style={{ flex: 1, minWidth: 0 }}>
        <Row alignItems="center" gap="spacing.general.x2" minWidth={0} width="100%">
          <ChannelAvatar
            channel={channel}
            image={viewModel.image}
            isGroupConversation={viewModel.isGroupConversation}
            myUserId={me?.id}
            showTransportSupportAvatar={viewModel.showTransportSupportAvatar}
            useHauteGaspesieQuickChatStyle={viewModel.useHauteGaspesieQuickChatStyle}
          />
          <ChannelIdentity
            availability={isUnavailable ? 'unavailable' : 'available'}
            groupSubtitle={groupSubtitle}
            interlocutor={interlocutor}
            viewModel={viewModel}
          />
        </Row>
      </Pressable>
      <ChannelTitleActions
        channel={channel}
        hasContextMenu={viewModel.hasContextMenu}
        isAdmin={isAdmin}
        onAddMembers={onAddMembers}
        onChevronPress={onChevronPress}
        onLeave={onLeave}
        onLinkOffer={onLinkOffer}
        onMarkAsUnread={onMarkAsUnread}
        onRename={onRename}
        onToggleSilenced={onToggleSilenced}
        showChevron={showChevron}
        showContextMenu={showContextMenu}
        silenced={silenced}
        useHauteGaspesieQuickChatStyle={viewModel.useHauteGaspesieQuickChatStyle}
      />
    </Row>
  );
}

export function ChannelTitleMenuButton({
  channel,
  silenced,
  onMarkAsUnread,
  onToggleSilenced,
  onAddMembers,
  onLeave,
  onLinkOffer,
  onRename,
  isAdmin,
}: ChannelTitleMenuButtonProps) {
  const navigation = useNavigation();
  const { t } = useTranslation('one-channel');
  const { width: windowWidth } = useWindowDimensions();
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const buttonRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  const hasContextMenu = onMarkAsUnread || onToggleSilenced || onAddMembers || onLeave || onLinkOffer || onRename;
  const isGroupConversation = channel.custom_type === CustomChannelType.Group;
  const effectiveSilenced = silenced ?? isChannelSilenced(channel);
  const canViewMembers = channel.custom_type === CustomChannelType.Group || channel.member_count > 2;

  const navigateToChatMembers = () => {
    navigateToChannelMembers(navigation, channel);
  };

  const handleMenuOpen = () => {
    buttonRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setMenuPosition({ top: pageY + height, right: windowWidth - pageX - width });
    });
    setIsContextMenuVisible((visible) => !visible);
  };

  const runAfterClosingMenu = (callback: () => void) => {
    setIsContextMenuVisible(false);
    setTimeout(callback, 300);
  };

  const handleToggleSilenced = () => {
    runAfterClosingMenu(() => onToggleSilenced?.(!effectiveSilenced));
  };

  const handleMarkAsUnread = () => {
    runAfterClosingMenu(() => onMarkAsUnread?.());
  };

  const openMembersSheet = () => {
    runAfterClosingMenu(navigateToChatMembers);
  };

  const handleAddMembers = () => {
    runAfterClosingMenu(() => onAddMembers?.());
  };

  const handleLeave = () => {
    runAfterClosingMenu(() => onLeave?.());
  };

  const handleLinkOffer = () => {
    runAfterClosingMenu(() => onLinkOffer?.());
  };

  const handleRename = () => {
    runAfterClosingMenu(() => onRename?.());
  };

  if (!hasContextMenu) {
    return null;
  }

  return (
    <>
      <View ref={buttonRef} style={{ flexShrink: 0, marginLeft: 'auto' }}>
        <Button
          accessibilityHint={t('chat.moreActionsHint', { ns: 'accessibility' })}
          accessibilityLabel={t('chat.moreActions', { ns: 'accessibility' })}
          accessibilityRole="button"
          onPress={handleMenuOpen}
        >
          <ContextualMenuButton dotSize={4} color={theme.colors['white-label.header.on-header']} />
        </Button>
      </View>
      <Modal
        transparent
        animationType="none"
        visible={isContextMenuVisible}
        onRequestClose={() => setIsContextMenuVisible(false)}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={() => setIsContextMenuVisible(false)} />
        {menuPosition ? (
          <Column
            backgroundColor="surface.default"
            borderRadius="radius.sm"
            elevation={5}
            gap="spacing.general.x4"
            minWidth={220}
            padding="spacing.general.x4"
            position="absolute"
            right={menuPosition.right}
            shadowColor="black"
            shadowOffset={{ width: 0, height: 0 }}
            shadowOpacity={0.15}
            shadowRadius={2.35}
            top={menuPosition.top}
          >
            <ChannelMenuContent
              canViewMembers={canViewMembers}
              channel={channel}
              effectiveSilenced={effectiveSilenced}
              handleAddMembers={handleAddMembers}
              handleLeave={handleLeave}
              handleLinkOffer={handleLinkOffer}
              handleMarkAsUnread={handleMarkAsUnread}
              handleRename={handleRename}
              handleToggleSilenced={handleToggleSilenced}
              isAdmin={isAdmin}
              isGroupConversation={isGroupConversation}
              onAddMembers={onAddMembers}
              onLeave={onLeave}
              onLinkOffer={onLinkOffer}
              onMarkAsUnread={onMarkAsUnread}
              onRename={onRename}
              onToggleSilenced={onToggleSilenced}
              openMembersSheet={openMembersSheet}
              silenced={silenced}
            />
          </Column>
        ) : null}
      </Modal>
    </>
  );
}
